import {
    AttributeDropdown,
    getNoChangeOption,
    getNotChosenOption,
    getPayslipDeliveryMethodOptions,
    getPersonnelManagerOptions,
} from '@Component/AttributeDropdown/AttributeDropdown';
import { UncontrolledForm } from '@Component/Form/UncontrolledForm';
import { AUTH_LEVEL_DEPARTMENT, useTskContext } from '@Hooks/useTskContext';
import { BodyDTO, EmployeeDTO, SalaryV2TransactionDTO } from '@Models/model.types';
import { SanitizedHTMLAndMarkdown } from '@Util/SanitizedHTMLAndMarkdown.tsx';
import { callApi, computeUrl, mapObjectToUrlSearchParams, MethodType } from '@Util/api';
import { mapValidationMessages } from '@Util/apiErrorUtils.ts';
import { createOrdinalFromArray } from '@Util/misc.ts';
import { APIUrls, DropdownUrls } from '@Util/urls.ts';
import { APIError, ListResponse, ResponseWrapper, showActionLogMessage } from '@tlx/astro-shared';
import {
    Alert,
    AlertContent,
    Button,
    Modal,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalLayout,
    ModalTitle,
} from '@tlx/atlas';
import { SpinnerButton } from '@tlx/tsk-shared';
import { Dispatch, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SWRInfiniteKeyedMutator } from 'swr/infinite';

export type ChangeEmployeeInfoDTO = {
    divisionId: string;
    departmentId: string;
    employeeCategoryId: string;
    wageSlipDeliveryMethod: string;
    employmentFormId: string;
    personnelManagerId: string;
};

export function ChangeEmployeeInfoModal({
    open,
    onClose,
    employeeIds,
    mutate,
    setValidationsMap,
}: {
    open: boolean;
    onClose: () => void;
    employeeIds: number[];
    mutate: SWRInfiniteKeyedMutator<ListResponse<EmployeeDTO>[]>;
    setValidationsMap: Dispatch<SetStateAction<Map<number, string[]>>>;
}) {
    const formId = 'changeEmployeeInfoForm';
    const intl = useIntl();
    const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);
    const [errorsToShowInModal, setErrorsToShowInModal] = useState<string[]>([]);
    const submitForm = async (_: HTMLFormElement, formData: { [p: string]: FormDataEntryValue }) => {
        if (Object.values(formData).every((value) => value === '-1')) {
            setErrorsToShowInModal([
                intl.formatMessage({
                    id: 'validation_form_not_completed',
                }),
            ]);
            return;
        }
        setValidationsMap(new Map());
        setAwaitingResponse(true);
        const changeEmployeeInfoDTO: ChangeEmployeeInfoDTO = {
            divisionId: formData.divisionDropdown as string,
            departmentId: formData.departmentDropdown as string,
            employeeCategoryId: formData.employeeCategoryDropdown as string,
            wageSlipDeliveryMethod: formData.wageslip_delivery_method_dropdown as string,
            employmentFormId: formData.form_of_employment_dropdown as string,
            personnelManagerId: formData.employeeAttestantDropdown as string,
        };
        const bodyDTO: BodyDTO = {
            employeeIds: employeeIds.join(','),
        };
        const { response, error } = await callApi<ResponseWrapper<SalaryV2TransactionDTO>>(
            computeUrl(APIUrls.CHANGE_EMPLOYEE_INFO, mapObjectToUrlSearchParams(changeEmployeeInfoDTO)),
            MethodType.PUT,
            JSON.stringify(bodyDTO),
        );
        setAwaitingResponse(false);
        if (error) {
            if (error instanceof APIError) {
                mapValidationMessages(
                    error.details.validationMessages,
                    setValidationsMap,
                    setErrorsToShowInModal,
                    intl.formatMessage({
                        id: 'validation_unexpected_error',
                    }),
                    onClose,
                );
            }
        }
        if (response) {
            onClose();
            mutate();
            showActionLogMessage({
                pageName: intl.formatMessage({
                    id: 'heading_employee_overview',
                }),
                date: new Date(),
                message: intl.formatMessage(
                    {
                        id:
                            employeeIds.length === 1
                                ? 'tsk_change_employment_success_singular'
                                : 'tsk_change_employment_success_plural',
                    },
                    createOrdinalFromArray([employeeIds.length]),
                ),
            });
        }
    };

    const { authLevelEmployeeOrWage } = useTskContext();

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!awaitingResponse) {
                    // to avoid user "cancelling" the dialog by clicking outside it
                    onClose();
                }
            }}
        >
            <ModalLayout>
                {!awaitingResponse && <ModalCloseButton />}
                <ModalTitle>
                    <FormattedMessage id={'text_change_employee_info'} />
                </ModalTitle>
                <ModalContent className={'tsk-width-600'}>
                    {errorsToShowInModal.length > 0 && (
                        <Alert variant="error" className={'atl-mb-24'}>
                            <AlertContent>
                                <SanitizedHTMLAndMarkdown content={errorsToShowInModal.join('\n')} />
                            </AlertContent>
                        </Alert>
                    )}
                    <UncontrolledForm submitForm={submitForm} id={formId}>
                        <div className="atl-flex atl-flex-col atl-gap-24">
                            <div className={'atl-flex atl-gap-16'}>
                                <AttributeDropdown
                                    text={'text_division'}
                                    id={'divisionDropdown'}
                                    url={DropdownUrls.DIVISIONS}
                                    options={getNoChangeOption(intl)}
                                />

                                <AttributeDropdown
                                    text={'text_department'}
                                    id={'departmentDropdown'}
                                    url={DropdownUrls.ACTIVE_DEPARTMENTS}
                                    options={getNoChangeOption(intl)}
                                    disabled={authLevelEmployeeOrWage <= AUTH_LEVEL_DEPARTMENT}
                                />
                            </div>
                            <div className={'atl-flex atl-gap-16'}>
                                <AttributeDropdown
                                    text={'text_employee_category'}
                                    id={'employeeCategoryDropdown'}
                                    url={DropdownUrls.EMPLOYEE_CATEGORIES}
                                    options={getNoChangeOption(intl)}
                                />
                                <AttributeDropdown
                                    text={'text_payslip_to'}
                                    id={'wageslip_delivery_method_dropdown'}
                                    options={[...getNoChangeOption(intl), ...getPayslipDeliveryMethodOptions(intl)]}
                                />
                            </div>
                            <div className={'atl-flex atl-gap-16'}>
                                <AttributeDropdown
                                    text={'text_form_of_employment'}
                                    id={'form_of_employment_dropdown'}
                                    url={DropdownUrls.FORM_OF_EMPLOYMENTS}
                                    options={[...getNoChangeOption(intl), ...getNotChosenOption(intl, '0')]}
                                />
                                <AttributeDropdown
                                    text={'text_employee_attestant'}
                                    id={'employeeAttestantDropdown'}
                                    url={DropdownUrls.EMPLOYEES}
                                    options={[...getPersonnelManagerOptions(intl)]}
                                />
                            </div>
                        </div>
                    </UncontrolledForm>
                </ModalContent>
                <ModalFooter className={'atl-flex atl-gap-8'}>
                    <SpinnerButton
                        className="atl-button--primary"
                        data-testid="change-employee-info-run-button"
                        type={'submit'}
                        form={formId}
                        disabled={awaitingResponse}
                        isLoading={awaitingResponse}
                    >
                        <FormattedMessage id={'button_save'} />
                    </SpinnerButton>
                    <Button
                        variant={'secondary'}
                        onClick={onClose}
                        data-testid="change-employee-info-modal-cancel-button"
                        disabled={awaitingResponse}
                    >
                        <FormattedMessage id={'text_cancel'} />
                    </Button>
                </ModalFooter>
            </ModalLayout>
        </Modal>
    );
}
